/* eslint-disable react/sort-comp */
import React from 'react'
import PropTypes from 'prop-types'
import NotificationPanel from './NotificationPanel'
import TagAnalytics from '../../lib/tag-analytics/v2'

import {
  APIClient,
  DEFAULT_AUTH_HEADER,
  DEFAULT_AUTH_SCHEME
} from '../../lib/api'

import {
  EMPTY_OBJECT_RESPONSE,
  hasReachedScrollViewport,
  canPerfomScrollEvent,
  deleteAllTokens,
  normalizeText
} from '../../lib/utils'

import { CDN_CUSTOM_EVENTS_NAMES, listenCustomEvent } from '../../lib/events'

import { getDeviceFingerprint } from '../../lib/fingerprint'

import {
  initialize as initializeFirebaseLib,
  registerServiceWorker as registerFirebaseServiceWorker,
  SERVICE_WORKER_FILENAME
} from '../../lib/firebasePushNotification'

import ErrorBoundary from '../ErrorBoundary/ErrorBoundary'

export const handleAnalytics = (eventName = 'customPageView', cdnMessage) => {
  TagAnalytics(
    eventName,
    {
      site: {
        subEnvironment: 'cdn'
      },
      page: {
        flowType: 'cockpit-cdn',
        pageType: 'cockpit-cdn',
        pageName: '/webmotors/cockpit/cdn',
        'pageName.tier1': 'cockpit',
        'pageName.tier2': 'cdn',
        'pageName.tier3': undefined,
        loadTime: window.performance.PerformanceNavigationTiming
      }
    },
    cdnMessage
  )
}

export const INTERECTED_FALLBACK_RESPONSES = {
  notDispatched: { data: 'not_dispatched' },
  alreadyInterected: { data: 'already_interected' }
}

export const DEFAULT_FILTER_OPTION = 'Todos'

// This is outside the main context to be persisted between mounts of the component
// THis should be reseted in every new script load (eg: page refresh)
let lastSubscribedToken

// To be used by tests
export const resetLastSubscribedToken = () => {
  lastSubscribedToken = null
}

class NotificationPanelContainer extends React.Component {
  constructor(props) {
    super(props)

    // HTTP request
    this.getNotifications = this.getNotifications.bind(this)
    this.getTotalUnread = this.getTotalUnread.bind(this)
    this.getProducts = this.getProducts.bind(this)
    this.postSubscribeUserToken = this.postSubscribeUserToken.bind(this)
    this.removeNegotiationById = this.removeNegotiationById.bind(this)

    // React setState
    this.setNotifications = this.setNotifications.bind(this)
    this.setTotalUnread = this.setTotalUnread.bind(this)
    this.setProducts = this.setProducts.bind(this)

    // Event handlers
    this.onHandleScroll = this.onHandleScroll.bind(this)
    this.onClickBell = this.onClickBell.bind(this)
    this.onHandleFilterOptions = this.onHandleFilterOptions.bind(this)
    this.onDeleteToken = this.onDeleteToken.bind(this)
    this.onClickAction = this.onClickAction.bind(this)

    this.firebaseInitialized = false

    this.lastScrollTimer = 0
  }

  state = {
    notifications: [],
    products: [],
    productId: null,
    isLoading: true,
    isSkeletonVisible: true,
    isPaginationFinished: false,
    canPaginate: false,
    totalUnread: 0,
    pageSize: 4,
    pageIndex: 1,
    hasError: false,
    filterLabel: DEFAULT_FILTER_OPTION
  }

  componentDidMount() {
    this.setAPIProps()
    this.registerServiceWorker()
    this.registerCustomEvents()

    const { getDataOnMount = true } = this.props

    // For testing this can be quite useful
    if (getDataOnMount) {
      this.getTotalUnread()
    }
  }

  updateNotificationInterectedAt(notificationId, actionId) {
    if (notificationId && actionId) {
      return this.apiClient.updateInterected(notificationId, actionId)
    }

    return Promise.resolve(INTERECTED_FALLBACK_RESPONSES.notDispatched)
  }

  onClickAction(action = {}) {
    const { notificationId, id: actionId, interectedAt = null } = action

    // If this action was already been interected in the past, doesn't update it again
    if (interectedAt) {
      return Promise.resolve(INTERECTED_FALLBACK_RESPONSES.alreadyInterected)
    }

    return this.updateNotificationInterectedAt(notificationId, actionId)
  }

  onDeleteToken() {
    const { firebaseConfig = {} } = this.props

    return deleteAllTokens(this.apiClient, firebaseConfig)
  }

  registerCustomEvents() {
    return listenCustomEvent(
      window,
      CDN_CUSTOM_EVENTS_NAMES.deleteAllTokens
    ).then(() => {
      this.onDeleteToken()
    })
  }

  setAPIProps() {
    const {
      apiHost,
      authToken,
      authScheme = DEFAULT_AUTH_SCHEME,
      authHeader = DEFAULT_AUTH_HEADER
    } = this.props

    this.apiClient = new APIClient(apiHost, authToken, authHeader, authScheme)
  }

  registerServiceWorker() {
    const {
      firebaseConfig = {},
      serviceWorkerFilename = SERVICE_WORKER_FILENAME,
      registerServiceWorker
    } = this.props

    // eslint-disable-next-line prefer-promise-reject-errors
    if (!registerServiceWorker) return Promise.resolve(null)

    return registerFirebaseServiceWorker(
      firebaseConfig,
      serviceWorkerFilename
    ).then((token) => {
      if (token) this.postSubscribeUserToken(token)

      return Promise.resolve(token)
    })
  }

  initializeFirebase() {
    if (this.firebaseInitialized) {
      return false
    }

    const {
      firebaseConfig = {},
      serviceWorkerFilename = SERVICE_WORKER_FILENAME
    } = this.props

    initializeFirebaseLib(firebaseConfig, serviceWorkerFilename).then(
      (token) => {
        console.debug(`[CDN DEBUG] Token = ${token}`)

        if (token) this.postSubscribeUserToken(token)

        this.firebaseInitialized = true
      }
    )

    this.firebaseInitialized = true

    return true
  }

  // [Section] HTTP requests

  async getTotalUnread() {
    const data = await this.apiClient
      .getUnreadNotificationsCount()
      .catch(() => EMPTY_OBJECT_RESPONSE)

    const { totalUnread = 0 } = data

    this.setTotalUnread(totalUnread)
  }

  async getProducts() {
    const data = await this.apiClient
      .getProducts()
      .catch(() => EMPTY_OBJECT_RESPONSE)

    const { products = [] } = data

    this.setProducts(products)
  }

  async getNotifications(isLoading = true) {
    const { pageSize, pageIndex, productId, notifications } = this.state

    // Indicate to users that something is about to happen
    this.setState({ isLoading })

    const data = await this.apiClient
      .getNotifications(productId, pageIndex, pageSize)
      .catch(() => {
        this.setState({
          hasError: true
        })

        return EMPTY_OBJECT_RESPONSE
      })
    const { notifications: requestNotifications = [], totalUnread } = data
    requestNotifications.forEach((notification) => {
      handleAnalytics(
        'cdnNotification',
        `notificacao-cdn-${normalizeText(notification?.title)}`
      )
    })
    // Merge all current loaded notifications + the notifications received in request
    const allNotifications = [...notifications, ...requestNotifications]

    // Determine if we already loaded all user's notifications
    const canPaginate = requestNotifications.length >= pageSize - 1

    // If user can't paginate it means this user reached the end of the pagination
    const isPaginationFinished = !canPaginate

    this.setNotifications(
      allNotifications,
      canPaginate,
      totalUnread,
      isPaginationFinished
    )
  }

  async postSubscribeUserToken(token) {
    // Doesn't process twice the same token
    if (token === lastSubscribedToken) return Promise.resolve(null)

    // Update the current user's so this request wont be dispatched again
    lastSubscribedToken = token

    const { productIdentifier } = this.props

    const deviceId = await getDeviceFingerprint()

    return this.apiClient.subscribeToken(token, productIdentifier, deviceId)
  }

  resetAndGetNotifications() {
    // Clear scroll timer to allow scrolling for new notifications
    this.lastScrollTimer = 0

    // Clear all current user notifications and pagination to GET then again
    return this.setState(
      {
        notifications: [],
        products: [],
        pageIndex: 1,
        productId: null,
        canPaginate: true,
        isPaginationFinished: false,
        isLoading: false,
        isSkeletonVisible: true,
        filterLabel: DEFAULT_FILTER_OPTION
      },
      () => {
        // Dispatch HTTP request to GET products in a clean state
        this.getProducts()

        // Dispatch HTTP request to GET notifications in a clean state
        this.getNotifications(true)
      }
    )
  }

  async removeNegotiationById(id) {
    try {
      await this.apiClient.removeNegotiationById({ id })

      this.setState({ notifications: [], isSkeletonVisible: true })
      this.getNotifications()
    } catch (err) {
      console.log('err', err)
    }
  }

  // [Section] React setState
  setNotifications(
    notifications,
    canPaginate,
    totalUnread = 0,
    isPaginationFinished = false
  ) {
    this.setState({
      notifications,
      canPaginate,
      totalUnread,
      isPaginationFinished,
      isLoading: false,
      isSkeletonVisible: false
    })
  }

  setTotalUnread(totalUnread) {
    this.setState({
      totalUnread
    })
  }

  setProducts(products) {
    this.setState({
      products
    })
  }

  setPagination(pageIndex, canPaginate = true, callback) {
    return this.setState(
      {
        pageIndex,
        canPaginate
      },
      () => callback && callback()
    )
  }

  // [Section] Event Handlers

  get canScroll() {
    const { isLoading, notifications, canPaginate } = this.state

    const hasNotification = !!notifications.length
    const canScroll = canPaginate && hasNotification && !isLoading

    return canScroll
  }

  onHandleScroll(event, ref, callback = null) {
    // Users can only paginate if they already have at least one notification
    if (!this.canScroll) return
    // Avoid to perform lots of computation by not allowing multiples scrolls per ms
    if (!canPerfomScrollEvent(event, this.lastScrollTimer)) return

    // Update last scroll timestamp reference to keep it stale
    this.lastScrollTimer = event.timeStamp

    if (hasReachedScrollViewport(event.target, ref)) {
      const { pageIndex } = this.state

      // This is the default behavior
      if (!callback) {
        // eslint-disable-next-line no-param-reassign
        callback = () => {
          this.getNotifications(true)
        }
      }

      this.setPagination(pageIndex + 1, true, callback)
    }
  }

  onClickBell() {
    this.initializeFirebase()
    this.resetAndGetNotifications()
  }

  onHandleFilterOptions(productId) {
    const { products } = this.state

    const defaultProduct = {
      name: DEFAULT_FILTER_OPTION
    }

    let itemProduct = defaultProduct

    if (productId) {
      itemProduct =
        products.find((item) => item.id === productId) || defaultProduct
    }
    handleAnalytics(`notificacao-cdn-${itemProduct.name.toLowerCase()}`)

    this.setState(
      {
        notifications: [],
        productId,
        pageIndex: 1,
        isSkeletonVisible: true,
        filterLabel: itemProduct.name
      },
      () => {
        this.getNotifications()
      }
    )
  }

  render() {
    const {
      notifications = [],
      products = [],
      isLoading = false,
      totalUnread = 0,
      hasError = false,
      isSkeletonVisible = false,
      isPaginationFinished = false,
      filterLabel = DEFAULT_FILTER_OPTION
    } = this.state

    const { env, sentryDSN, bellButtonIcon } = this.props

    return (
      <ErrorBoundary env={env} sentryDSN={sentryDSN}>
        <NotificationPanel
          onClickAction={this.onClickAction}
          hasError={hasError}
          handleScroll={this.onHandleScroll}
          onClickBell={this.onClickBell}
          onHandleFilterOptions={this.onHandleFilterOptions}
          labelFilterProducts={filterLabel}
          bellButtonIcon={bellButtonIcon}
          notifications={notifications}
          isLoading={isLoading}
          isSkeletonVisible={isSkeletonVisible}
          isPaginationFinished={isPaginationFinished}
          totalUnread={totalUnread}
          products={products}
          removeNegotiationById={this.removeNegotiationById}
        />
      </ErrorBoundary>
    )
  }
}

NotificationPanelContainer.defaultProps = {
  getDataOnMount: true,
  bellButtonIcon: null,
  env: null,
  authToken: null,
  registerServiceWorker: true,
  authScheme: DEFAULT_AUTH_SCHEME,
  authHeader: DEFAULT_AUTH_HEADER,
  serviceWorkerFilename: SERVICE_WORKER_FILENAME,
  productIdentifier: null,
  firebaseConfig: {}
}

NotificationPanelContainer.propTypes = {
  productIdentifier: PropTypes.string,
  getDataOnMount: PropTypes.bool,
  env: PropTypes.string,
  sentryDSN: PropTypes.string,
  authHeader: PropTypes.string,
  authScheme: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  authToken: PropTypes.string,
  apiHost: PropTypes.string.isRequired,
  bellButtonIcon: PropTypes.string,
  serviceWorkerFilename: PropTypes.string,
  firebaseConfig: PropTypes.shape({}),
  registerServiceWorker: PropTypes.bool
}

export default NotificationPanelContainer
